import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { useUserPermissions } from "@/hooks/useUserRole";
import { useWorkspace, type WorkspaceRole } from "@/hooks/useWorkspace";

export type ActiveScope = "org" | "association" | "platform";

export interface ActiveContext {
  /** Current scope type */
  scope: ActiveScope;
  /** Active org or association ID */
  scopeId: string;
  /** Active workspace role */
  activeWorkspace: WorkspaceRole | null;
  /** User's resolved permissions for current context */
  permissions: string[];
  /** Whether user has a specific permission */
  can: (permission: string) => boolean;
  /** Current role_type from organization_users */
  roleType: string | null;
  /** Whether the context is still loading */
  isLoading: boolean;
  /** Whether user is an admin in current context */
  isAdmin: boolean;
}

/**
 * Central hook that resolves the active user context:
 * - What scope are they in (org / association / platform)?
 * - What workspace are they using?
 * - What can they do?
 *
 * This replaces ad-hoc permission checks scattered across components.
 */
export const useActiveContext = (): ActiveContext => {
  const { user } = useAuth();
  const { orgId, loading: orgLoading } = useOrg();
  const { data: perms, isLoading: permsLoading } = useUserPermissions();
  const { activeWorkspace, isLoading: wsLoading } = useWorkspace();

  const isLoading = orgLoading || permsLoading || wsLoading || !user;

  const scope: ActiveScope = useMemo(() => {
    if (!orgId) return "platform";
    if (perms?.role_type === "association_admin" || perms?.role_type === "association_staff") {
      return "association";
    }
    return "org";
  }, [orgId, perms?.role_type]);

  const permissions = useMemo(() => perms?.permissions ?? [], [perms?.permissions]);

  const can = useMemo(
    () => (permission: string) => {
      if (perms?.is_admin) return true;
      return permissions.includes(permission);
    },
    [permissions, perms?.is_admin],
  );

  return {
    scope,
    scopeId: orgId,
    activeWorkspace,
    permissions,
    can,
    roleType: perms?.role_type ?? null,
    isAdmin: !!perms?.is_admin,
    isLoading,
  };
};
